import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { storageService } from '@/services/local-storage';
import type { Evaluation } from '@/types/evaluation.types';

/**
 * Hook to save (create or update) an evaluation
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```tsx
 * function EvaluationForm() {
 *   const saveEvaluation = useSaveEvaluation();
 *
 *   const handleSubmit = (evaluation: Evaluation) => {
 *     saveEvaluation.mutate(evaluation, {
 *       onSuccess: () => {
 *         toast.success('Evaluation saved!');
 *         navigate('/');
 *       },
 *       onError: (error) => {
 *         toast.error(`Failed to save: ${error.message}`);
 *       },
 *     });
 *   };
 *
 *   return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
export function useSaveEvaluation(): UseMutationResult<void, Error, Evaluation> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluation: Evaluation) => {
      storageService.saveEvaluation(evaluation);
      return Promise.resolve();
    },
    onSuccess: (_, evaluation) => {
      // Invalidate and refetch evaluation list
      queryClient.invalidateQueries({ queryKey: queryKeys.evaluations.all });

      // Update the specific evaluation in cache
      queryClient.invalidateQueries({ queryKey: queryKeys.evaluations.detail(evaluation.id) });
    },
  });
}

/**
 * Hook to delete an evaluation
 *
 * @returns Mutation result with mutate function
 *
 * @example
 * ```tsx
 * function EvaluationCard({ evaluation }: { evaluation: Evaluation }) {
 *   const deleteEvaluation = useDeleteEvaluation();
 *
 *   const handleDelete = () => {
 *     deleteEvaluation.mutate(evaluation.id, {
 *       onSuccess: () => {
 *         toast.success('Evaluation deleted');
 *       },
 *     });
 *   };
 *
 *   return (
 *     <Card>
 *       <Button onClick={handleDelete} disabled={deleteEvaluation.isPending}>
 *         Delete
 *       </Button>
 *     </Card>
 *   );
 * }
 * ```
 */
export function useDeleteEvaluation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      storageService.deleteEvaluation(id);
      return Promise.resolve();
    },
    onSuccess: (_, id) => {
      // Invalidate and refetch evaluation list
      queryClient.invalidateQueries({ queryKey: queryKeys.evaluations.all });

      // Remove the deleted evaluation from cache
      queryClient.removeQueries({ queryKey: queryKeys.evaluations.detail(id) });

      // Also invalidate related scorecards
      queryClient.invalidateQueries({ queryKey: queryKeys.scorecards.byEvaluation(id) });
    },
  });
}

